import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CircleDollarSign,
  Edit3,
  Eye,
  EyeOff,
  Loader2,
  Plus,
  Star,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PricingPlanFormDialog } from "@/components/pricing-plan-form-dialog";
import { cn } from "@/lib/cn";
import {
  deletePricingPlan,
  listAdminPlans,
  type PricingPlan,
} from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";

function formatPriceShort(p: PricingPlan): string {
  if (p.priceCents === 0) return "무료";
  const v = (p.priceCents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: p.currency || "USD",
    maximumFractionDigits: 2,
  });
  const period = p.billingPeriod === "year" ? "/년" : "/월";
  return `${v}${p.isPerSeat ? "/좌석" : ""}${period}`;
}

export function AdminPricingPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PricingPlan | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const plansQuery = useQuery({
    queryKey: ["pricing-plans-admin"],
    queryFn: listAdminPlans,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePricingPlan(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pricing-plans-admin"] });
      void queryClient.invalidateQueries({ queryKey: ["pricing-plans-public"] });
      setDeletingId(null);
    },
    onError: (err) => {
      alert(extractErrorMessage(err, "삭제에 실패했습니다"));
      setDeletingId(null);
    },
  });

  const onSaved = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["pricing-plans-admin"] });
    void queryClient.invalidateQueries({ queryKey: ["pricing-plans-public"] });
  };

  const plans = plansQuery.data ?? [];

  return (
    <div className="space-y-6 p-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">관리자</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">가격 관리</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            가격 / 기능 / 활성 토글을 즉시 변경. 랜딩의 /pricing 페이지에 바로 반영됩니다.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="gap-2"
        >
          <Plus className="h-4 w-4" /> 새 플랜
        </Button>
      </header>

      <PricingPlanFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={onSaved}
      />
      <PricingPlanFormDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
        {...(editing ? { initial: editing } : {})}
      />

      {plansQuery.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> 불러오는 중…
        </p>
      )}

      {!plansQuery.isLoading && plans.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <CircleDollarSign className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
          <p className="mt-3 font-medium">아직 플랜이 없습니다</p>
          <p className="mt-1 text-sm text-muted-foreground">
            "새 플랜" 버튼으로 첫 플랜을 등록해 보세요.
          </p>
        </div>
      )}

      {!plansQuery.isLoading && plans.length > 0 && (
        <ul className="space-y-3">
          {plans.map((p) => (
            <li
              key={p.id}
              className="rounded-lg border border-border bg-card p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-semibold">{p.name}</span>
                    <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground">
                      {p.slug}
                    </span>
                    {p.highlighted && (
                      <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                        <Star className="h-3 w-3" /> 추천
                      </span>
                    )}
                    {p.active ? (
                      <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-600 dark:text-emerald-400">
                        <Eye className="h-3 w-3" /> 노출
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        <EyeOff className="h-3 w-3" /> 숨김
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-2xl font-bold tracking-tight">
                    {formatPriceShort(p)}
                  </p>
                  {p.description && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {p.description}
                    </p>
                  )}
                  {p.features.length > 0 && (
                    <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                      {p.features.slice(0, 4).map((f, i) => (
                        <li key={i}>• {f}</li>
                      ))}
                      {p.features.length > 4 && (
                        <li>… 외 {p.features.length - 4}개</li>
                      )}
                    </ul>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditing(p)}
                  >
                    <Edit3 className="h-3.5 w-3.5" /> 수정
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={deleteMutation.isPending && deletingId === p.id}
                    onClick={() => {
                      if (
                        confirm(
                          `정말 ${p.name}(${p.slug}) 플랜을 삭제할까요? 사용자 가입자가 있다면 영향을 받을 수 있습니다.`,
                        )
                      ) {
                        setDeletingId(p.id);
                        deleteMutation.mutate(p.id);
                      }
                    }}
                  >
                    {deleteMutation.isPending && deletingId === p.id ? (
                      <Loader2
                        className={cn("h-3.5 w-3.5 animate-spin")}
                      />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                    삭제
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
